import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {FlashList} from "@shopify/flash-list";
import type React from "react";
import {memo, useCallback, useMemo, useState} from "react";
import {FlatList} from "react-native";
import {KeyboardAwareScrollView} from "react-native-keyboard-controller";

import type {Listing} from "../../models/listing";
import {useUserContext} from "../../react";
import SearchInput from "../SearchInput";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

import ListingGroupItem from "./ListingGroupItem";
import ListingItem from "./ListingItem";
import useFilteredListings from "./useFilteredListings";
import useFilteredListingGroup from "./useFilterListingGroup";

// the `FlashList` is much faster on the native app
// while this list doesn't work well with `react-native-web` due to inline styles
const List = process.env.IS_WEB ? FlatList : FlashList;
const style = tva({base: "flex-1"});

type Props = {
    onPressListing: (listing: Listing) => void;
    className?: string;
} & StrictUnion<
    | {
          enableListingGroups: true;
          onPressListingGroup: (id: string) => void;
      }
    | {
          enableListingGroups?: false;
      }
>;

const ListingsAndGroups: React.FC<Props> = ({
    className,
    enableListingGroups = false,
    onPressListing,
    onPressListingGroup
}) => {
    const {visibleListings, listingGroups} = useUserContext();
    const [query, setQuery] = useState("");

    const filteredListings = useFilteredListings(visibleListings, query);
    const filteredListingGroups = useFilteredListingGroup(listingGroups, query);

    const handleClearSearch = useCallback(() => setQuery(""), []);

    const listingsAndGroups = useMemo(() => {
        if (enableListingGroups) {
            if (visibleListings.length > 0) {
                return [
                    "Listing Groups",
                    ...[
                        {
                            _id: "all",
                            name: "All Listings",
                            userID: "",
                            listingIDs: [],
                            listings: []
                        } as (typeof filteredListingGroups)[0],
                        ...filteredListingGroups
                    ],
                    "Listings",
                    ...filteredListings
                ];
            }
            return ["Listing Groups", ...filteredListingGroups, "Listings", ...filteredListings];
        }
        return ["Listings", ...filteredListings];
    }, [enableListingGroups, visibleListings.length, filteredListings, filteredListingGroups]);

    return (
        <VStack
            className={style({
                className
            })}
        >
            <VStack className="min-h-[auto] px-5 py-4 border-b border-solid border-blueGray-200">
                <SearchInput
                    query={query}
                    testID="input-search-listing"
                    onChangeText={setQuery}
                    onClear={handleClearSearch}
                />
            </VStack>
            <List
                contentContainerClassName="p-5"
                data={listingsAndGroups}
                renderItem={({item}) => {
                    if (typeof item === "string") {
                        return (
                            <Text className="pb-5" color="blueGray.800" variant="md">
                                {item}
                            </Text>
                        );
                    }
                    if ("listingIDs" in item) {
                        return (
                            <ListingGroupItem
                                key={item._id}
                                count={
                                    "uniqueMessageRulesCount" in item
                                        ? Number(item.uniqueMessageRulesCount)
                                        : undefined
                                }
                                id={item._id}
                                label={item.name}
                                onPress={onPressListingGroup}
                            />
                        );
                    }
                    return <ListingItem listing={item} onPress={onPressListing} />;
                }}
                renderScrollComponent={
                    process.env.IS_WEB ? undefined : props => <KeyboardAwareScrollView {...props} />
                }
                testID="listings-and-groups"
                // specific for the `FlashList`
                // https://shopify.github.io/flash-list/docs/guides/section-list/
                // getItemType={item => {
                //     if (typeof item === "string") {
                //         return "sectionHeader";
                //     }
                //     return "row";
                // }}
            />
        </VStack>
    );
};

export default memo(ListingsAndGroups);
